import { toISODate } from '@/client/lib/dates';
import type { RouterOutputs } from '@/client/utils/trpc';

type Entry = RouterOutputs['timesheets']['listEntries'][number];

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface WeekSummaryStripProps {
  entries: Entry[];
  weekFrom: string;
  weekSubmitted: boolean;
}

export function WeekSummaryStrip({ entries, weekFrom, weekSubmitted }: WeekSummaryStripProps) {
  const totalHours = entries.reduce((s, e) => s + e.hours, 0);

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(`${weekFrom}T00:00:00`);
    d.setDate(d.getDate() + i);
    const iso = toISODate(d);
    return {
      iso,
      label: DAY_ABBR[d.getDay()],
      hours: entries.filter((e) => e.date === iso).reduce((s, e) => s + e.hours, 0),
    };
  });

  const pct = Math.min((totalHours / 40) * 100, 100);

  return (
    <div className="flex items-center gap-5 rounded-lg border bg-card px-5 py-4">
      {/* Total hours */}
      <div className="shrink-0">
        <p className="text-xs text-muted-foreground">This week</p>
        <div className="mt-0.5 flex items-baseline gap-2">
          <span className="text-[28px] font-semibold tabular-nums leading-none">
            {totalHours.toFixed(1)}h
          </span>
          <span className="text-sm text-muted-foreground">of 40h target</span>
        </div>
      </div>

      {/* Progress bar + per-day breakdown */}
      <div className="ml-4 min-w-0 flex-1">
        <div className="h-2 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="mt-2 flex justify-between">
          {days.map((day) => (
            <div key={day.iso} className="text-center text-xs text-muted-foreground">
              <div className="font-medium">{day.label}</div>
              <div className="tabular-nums">{day.hours > 0 ? `${day.hours.toFixed(1)}h` : '—'}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Status */}
      <div className="shrink-0 border-l pl-5">
        <p className="text-xs text-muted-foreground">Status</p>
        {weekSubmitted ? (
          <span className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-400">
            <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
            Submitted
          </span>
        ) : (
          <span className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400">
            <span className="h-1.5 w-1.5 rounded-full bg-yellow-500" />
            Draft · not submitted
          </span>
        )}
      </div>
    </div>
  );
}
